/**
 * Visuel de la carte cadeau Bocuse — recto/verso avec flip 3D au tap.
 * Reproduit la composition de la carte officielle (marine + signature rouge).
 * Pas d'image externe : tout est dessiné en CSS/SVG (charte tenant).
 */
import { useState } from 'react';
import { useBranding } from '@alterrego/module-branding';
import { useI18n } from '../i18n';
import './GiftCardVisual.css';

/** QR code factice : finder patterns + matrice déterministe (placeholder visuel). */
function QrPlaceholder() {
  const N = 21;
  const cells: { x: number; y: number }[] = [];
  // Matrice pseudo-aléatoire mais déterministe (pas de Math.random → SSR-safe).
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const inFinder =
        (x < 7 && y < 7) || (x >= N - 7 && y < 7) || (x < 7 && y >= N - 7);
      if (inFinder) continue;
      if (((x * 7 + y * 13 + x * y * 3) % 5) === 0) cells.push({ x, y });
    }
  }
  const finder = (ox: number, oy: number) => (
    <>
      <rect x={ox} y={oy} width={7} height={7} fill="#1B2A47" />
      <rect x={ox + 1} y={oy + 1} width={5} height={5} fill="#fff" />
      <rect x={ox + 2} y={oy + 2} width={3} height={3} fill="#1B2A47" />
    </>
  );
  return (
    <svg className="gc-qr" viewBox={`0 0 ${N} ${N}`} aria-hidden="true" shapeRendering="crispEdges">
      <rect x={0} y={0} width={N} height={N} fill="#fff" />
      {cells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width={1} height={1} fill="#1B2A47" />
      ))}
      {finder(0, 0)}
      {finder(N - 7, 0)}
      {finder(0, N - 7)}
    </svg>
  );
}

interface GiftCardVisualProps {
  /** Code court figurant au dos (placeholder). */
  code?: string;
  /** Numéro de carte figurant au dos (placeholder). */
  number?: string;
}

export function GiftCardVisual({ code = '4444', number = '12345' }: GiftCardVisualProps) {
  const { t } = useI18n();
  const { branding } = useBranding();
  const [flipped, setFlipped] = useState(false);

  return (
    <div className="gc-visual">
      <button
        type="button"
        className={`gc-card${flipped ? ' is-flipped' : ''}`}
        onClick={() => setFlipped((f) => !f)}
        aria-label={t('gc_flip_hint')}
        aria-pressed={flipped}
      >
        <div className="gc-card-inner">
          {/* ── Recto ── */}
          <div className="gc-face gc-face--front">
            <div className="gc-front-brand">
              <span className="gc-front-overline">{t('card_overline')}</span>
              <span className="gc-front-signature">{branding.shortName}</span>
            </div>
            <div className="gc-front-voucher" aria-hidden="true">
              {t('card_voucher')}
            </div>
          </div>

          {/* ── Verso ── */}
          <div className="gc-face gc-face--back">
            <div className="gc-back-head">
              <p className="gc-back-title">{t('card_back_title')}</p>
              <p className="gc-back-valid">{t('card_back_valid')}</p>
              <p className="gc-back-exclusion">{t('card_back_exclusion')}</p>
            </div>

            <div className="gc-back-body">
              <div className="gc-back-qr">
                <QrPlaceholder />
                <div className="gc-back-ids">
                  <span>{t('card_back_code')} : {code}</span>
                  <span>{t('card_back_number')} {number}</span>
                </div>
              </div>
              <div className="gc-back-texts">
                <div className="gc-back-block">
                  <span className="gc-back-block-title">{t('card_back_use_title')}</span>
                  <span className="gc-back-block-body">{t('card_back_use_body')}</span>
                </div>
                <div className="gc-back-block">
                  <span className="gc-back-block-title">{t('card_back_conditions_title')}</span>
                  <span className="gc-back-block-body">{t('card_back_conditions_body')}</span>
                </div>
              </div>
            </div>

            <div className="gc-back-foot">
              <p className="gc-back-quote">{t('card_quote')}</p>
              <p className="gc-back-sign">{branding.founder}</p>
              <p className="gc-back-url">{branding.website}</p>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}
